/**
 * Color scheme: one contract for every YAGNI surface (ADR-0045).
 *
 * The preference is three-state. "system" follows whatever the host says
 * (the OS via `prefers-color-scheme`, or the editor inside VS Code); "light"
 * and "dark" pin a scheme. The resolved scheme is written to the root element
 * as `data-theme="light|dark"` plus the `color-scheme` property, and every
 * stylesheet keys its dark values on that attribute alone. CSS never reads the
 * media query directly, so there is exactly one place the answer comes from.
 *
 * Nothing here touches `window` or `document` until `start()` is called, so
 * the store is safe to import from tests that run in Node. The twin of this
 * file lives at `packages/desktop/src/lib/theme.ts`; keep the contract (the
 * preference names, the attribute, the storage shape) identical.
 */

export type ThemePreference = "system" | "light" | "dark";
export type ColorScheme = "light" | "dark";

export const THEME_PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"];
export const THEME_ATTRIBUTE = "data-theme";
export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";
/** The web app's key. The desktop and the editor panel use their own. */
export const THEME_STORAGE_KEY = "yagni.theme";

export const THEME_PREFERENCE_LABELS: Record<ThemePreference, string> = {
  system: "Match system",
  light: "Light",
  dark: "Dark",
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function resolveColorScheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ColorScheme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light";
  return preference;
}

/** A storage that may not exist (tests, a private window, a locked-down webview). */
export interface ThemeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

function safeLocalStorage(): ThemeStorage | null {
  // Through `window`, not the bare global: Node 24 exposes an experimental
  // `globalThis.localStorage` that warns when touched without a backing file.
  try {
    return globalThis.window?.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Never throws; anything unreadable is "system". */
export function readThemePreference(
  storage: ThemeStorage | null = safeLocalStorage(),
  key: string = THEME_STORAGE_KEY,
): ThemePreference {
  if (!storage) return "system";
  try {
    const raw = storage.getItem(key);
    return isThemePreference(raw) ? raw : "system";
  } catch {
    return "system";
  }
}

/** "system" is the default, so it is stored as an absence rather than a value. */
export function writeThemePreference(
  preference: ThemePreference,
  storage: ThemeStorage | null = safeLocalStorage(),
  key: string = THEME_STORAGE_KEY,
): void {
  if (!storage) return;
  try {
    if (preference === "system") storage.removeItem(key);
    else storage.setItem(key, preference);
  } catch (err) {
    // A denied quota costs the memory of the choice, never the choice itself.
    // Named, so "it applied but never stuck" is a storage report, not a bug hunt.
    console.debug("[theme] could not persist preference", preference, err);
  }
}

/** Stamp the resolved scheme where the stylesheets and the browser read it. */
export function applyColorScheme(scheme: ColorScheme, root: HTMLElement): void {
  root.setAttribute(THEME_ATTRIBUTE, scheme);
  root.style.colorScheme = scheme;
}

/**
 * Where "system" gets its answer. The default asks the OS through the media
 * query; a host that knows better (the VS Code panel, which should follow the
 * editor's theme rather than the desktop's) supplies its own.
 */
export interface SystemSchemeSource {
  prefersDark: () => boolean;
  subscribe: (listener: () => void) => () => void;
}

export function mediaQuerySource(win: Window | undefined = globalThis.window): SystemSchemeSource {
  const query = typeof win?.matchMedia === "function" ? win.matchMedia(DARK_SCHEME_QUERY) : null;
  return {
    prefersDark: () => query?.matches ?? false,
    subscribe: (listener) => {
      if (!query) return () => {};
      query.addEventListener("change", listener);
      return () => query.removeEventListener("change", listener);
    },
  };
}

export interface ThemeState {
  preference: ThemePreference;
  scheme: ColorScheme;
}

export interface ThemeStore {
  getSnapshot: () => ThemeState;
  setPreference: (preference: ThemePreference) => void;
  subscribe: (listener: () => void) => () => void;
  /**
   * Read the saved preference, stamp the document, and follow the system and
   * other tabs until the returned function is called. Idempotent.
   */
  start: () => () => void;
}

export interface ThemeStoreOptions {
  storage?: ThemeStorage | null;
  storageKey?: string;
  system?: SystemSchemeSource;
  /** The element that carries `data-theme`; the document root by default. */
  root?: () => HTMLElement | null;
  /** Cross-tab sync; the web app listens to `storage`, a webview has no peers. */
  window?: Window | null;
}

export function createThemeStore(options: ThemeStoreOptions = {}): ThemeStore {
  const storageKey = options.storageKey ?? THEME_STORAGE_KEY;
  const storage = options.storage === undefined ? safeLocalStorage() : options.storage;
  let system: SystemSchemeSource | null = options.system ?? null;
  const rootOf = options.root ?? (() => globalThis.document?.documentElement ?? null);
  const listeners = new Set<() => void>();

  let state: ThemeState = { preference: "system", scheme: "light" };
  let stop: (() => void) | null = null;

  const systemSource = (): SystemSchemeSource => {
    if (!system) system = mediaQuerySource();
    return system;
  };

  const emit = () => {
    for (const listener of listeners) listener();
  };

  const recompute = (preference: ThemePreference) => {
    const scheme = resolveColorScheme(preference, systemSource().prefersDark());
    if (state.preference === preference && state.scheme === scheme) return;
    state = { preference, scheme };
    const root = rootOf();
    if (root) applyColorScheme(scheme, root);
    emit();
  };

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setPreference: (preference) => {
      writeThemePreference(preference, storage, storageKey);
      recompute(preference);
    },
    start: () => {
      if (stop) return stop;
      // Stamp unconditionally on the first start: the state may already say
      // "light" while the element still carries nothing.
      const preference = readThemePreference(storage, storageKey);
      state = { preference, scheme: resolveColorScheme(preference, systemSource().prefersDark()) };
      const root = rootOf();
      if (root) applyColorScheme(state.scheme, root);
      emit();

      const unsubscribeSystem = systemSource().subscribe(() => recompute(state.preference));
      const win = options.window === undefined ? globalThis.window : options.window;
      const onStorage = (event: StorageEvent) => {
        if (event.key !== null && event.key !== storageKey) return;
        recompute(readThemePreference(storage, storageKey));
      };
      win?.addEventListener("storage", onStorage);

      stop = () => {
        unsubscribeSystem();
        win?.removeEventListener("storage", onStorage);
        stop = null;
      };
      return stop;
    },
  };
}

/** The app's store. `main.tsx` starts it before the first render. */
export const themeStore: ThemeStore = createThemeStore();
